import React, { useEffect, useRef } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select from "react-select";

const TableVisualization = ({
    indicators,
    locations,
    onSubmit, // Handler from parent - receives prepared data
    localSelectedLocations,
    setLocalSelectedLocations,
    selectedItems,
    setSelectedItems
}) => {
    const masterRef = useRef(null);
    const tableConfig = useSelector((state) => state.mapConfig.tableConfig);

    const geoLevelLabel = (level) => {
        switch (level) {
            case 1: return "National";
            case 2: return "State";
            case 3: return "District";
            case 4: return "ULG";
            default: return "Unknown";
        }
    };

    // Get consistent indicator ID
    const getIndicatorId = (indicator) =>
        indicator?.value?.toString() ||
        indicator?.indicator_code?.toString() ||
        "";

    useEffect(() => {
        if (!locations || locations.length === 0) return;

        const validOptions = [
            {
                label: `All States / UTs of India`,
                value: 1000, geo_level: 1, type: "Children"
            },
            ...locations.map(f => ({
                value: f.properties?.code,
                label: `${f.name || f.properties?.name} (${geoLevelLabel(f.geo_level_code || f.geo_level || f.properties?.geo_level_code || 4)})`,
                geo_level: f.geo_level_code || f.geo_level || f.properties?.geo_level_code || 4,
                type: ""
            }))
        ];

        // Filter out any selected locations that no longer exist
        const filteredSelected = localSelectedLocations.filter(selected =>
            validOptions.some(opt => opt.value === selected.value && opt.type === selected.type)
        );

        if (filteredSelected.length !== localSelectedLocations.length) {
            setLocalSelectedLocations(filteredSelected);
        }
    }, [locations]);

    // Collect all indicator + subIndicator values
    const getAllValues = () =>
        indicators.flatMap((item) => {
            const indicator = item.colorIndicator || item;
            return [
                getIndicatorId(indicator),
                ...(indicator?.sub_indicators?.map((sub) =>
                    sub.code?.toString()
                ) || []),
            ].filter(Boolean);
        });

    // Update indeterminate state on header checkbox
    useEffect(() => {
        if (!masterRef.current) return;

        const allValues = getAllValues();
        const selected = selectedItems || [];

        if (selected.length === 0) {
            masterRef.current.indeterminate = false;
            masterRef.current.checked = false;
        } else if (allValues.every((v) => selected.includes(v))) {
            masterRef.current.indeterminate = false;
            masterRef.current.checked = true;
        } else {
            masterRef.current.indeterminate = true;
            masterRef.current.checked = false;
        }
    }, [selectedItems, indicators]);

    // Toggle all indicators
    const toggleAll = (checked) => {
        const allValues = getAllValues();
        if (checked) {
            setSelectedItems([...new Set(allValues)]);
        } else {
            setSelectedItems([]);
        }
    };

    // Toggle a single indicator (with its subIndicators)
    const toggleOverall = (indicator, checked) => {
        const id = getIndicatorId(indicator);
        if (!id) return;

        const selected = Array.isArray(selectedItems) ? selectedItems : [];
        const allValues = [
            id,
            ...(indicator.sub_indicators?.map((sub) => sub.code?.toString()) || []),
        ].filter(Boolean);

        if (checked) {
            setSelectedItems([...new Set([...selected, ...allValues])]);
        } else {
            setSelectedItems(selected.filter((v) => !allValues.includes(v)));
        }
    };

    // Handle reset
    const handleReset = () => {
        setLocalSelectedLocations([]);
        setSelectedItems([]);
    };

    // Data patch/restoration logic
    useEffect(() => {
        if (!tableConfig) return;

        if (tableConfig?.indicators?.length > 0) {
            if (tableConfig.selectedLocations) {
                setLocalSelectedLocations(tableConfig.selectedLocations);
            }
            // FIXED: Replace instead of append
            setSelectedItems(tableConfig.indicators);
        }
    }, [tableConfig, indicators]); // FIXED: Added indicators dependency

    // Submit - prepare data and call parent handler (NO API CALL HERE)
    const handleTableChartSubmit = () => {
        // Validate
        if (!Array.isArray(localSelectedLocations) || localSelectedLocations.length === 0) {
            console.error("Error: No locations selected");
            return;
        }

        if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
            console.error("Error: No indicators selected");
            return;
        }

        // Prepare data
        const lgdDatas = localSelectedLocations.map((item) => ({
            code: item.value,
            geo_level_code: item.geo_level,
            type: item.type || "",
            children_geo_level: item.children_geo_level
        }));

        const payload = {
            lgd_codes: lgdDatas,
            indicators: selectedItems,
        };

        const formattedPayload = {
            location_info: localSelectedLocations,
            Indicator_info: indicators.reduce((acc, cur) => {
                acc[cur.indicator_code] = cur;
                return acc;
            }, {})
        };

        const reduxPayload = {
            visualizationType: "table",
            selectedLocations: localSelectedLocations,
            indicators: selectedItems,
        };

        // Call parent submit handler with prepared data
        onSubmit({ payload, formattedPayload, reduxPayload });
    };

    return (
        <div className="cardbg p-3">
            <Form.Label>Table Configuration</Form.Label>

            {/* Location Selector */}
            <div className="col-md-6 mb-3">
                <Form.Group>
                    <Form.Label>Location</Form.Label>
                    <Select
                        isMulti
                        options={[
                            {
                                label: `All States / UTs of India`,
                                value: 1000,
                                geo_level: 1,
                                type: "Children",
                                children_geo_level: 2
                            },
                            {
                                label: `All district of India`,
                                value: 1000,
                                geo_level: 1,
                                type: "Children",
                                children_geo_level: 3
                            },
                            ...(locations.length > 0
                                ? locations.flatMap((f) => {
                                    const name = f.name || f.properties?.name;
                                    const code = f.properties?.code || f.id;
                                    const geoLevel = f.properties?.geo_level_code;
                                    const type = "Children";
                                    const children_geo_level = geoLevel + 1;

                                    if (geoLevel === 2) {
                                        return [
                                            {
                                                label: `All Districts of ${name}`,
                                                value: code,
                                                geo_level: 2,
                                                type,
                                                children_geo_level
                                            },
                                        ];
                                    }
                                    return [];
                                })
                                : []),
                            {
                                label: "India (National)",
                                value: 1000,
                                geo_level: 1,
                                type: "",
                                children_geo_level: null
                            },
                            ...locations.map((f) => ({
                                value: f.properties?.code || f.id,
                                label: `${f.name || f.properties?.name} (${geoLevelLabel(f.properties?.geo_level_code || f.geo_level || 4)})`,
                                geo_level: f.properties?.geo_level_code || f.geo_level,
                                type: "",
                                children_geo_level: null,
                            })),
                        ]}
                        onChange={setLocalSelectedLocations}
                        value={localSelectedLocations}
                        placeholder="Choose Location"
                        getOptionValue={(e) => `${e.value}-${e.children_geo_level}`}
                        getOptionLabel={(e) => e.label}
                    />
                </Form.Group>
            </div>

            {/* Indicator Table */}
            <Table className="bg-white" bordered hover size="sm">
                <thead>
                    <tr>
                        <th>S.No</th>
                        <th>Indicator</th>
                        <th>
                            <Form.Check
                                type="checkbox"
                                label={selectedItems?.length > 0 ? "Deselect all" : "Select all"}
                                ref={masterRef}
                                onChange={(e) => toggleAll(e.target.checked)}
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {indicators.slice(0, indicators?.length).map((item, index) => {
                        const indicator = item.colorIndicator || item;
                        if (!indicator) return null;

                        const id = getIndicatorId(indicator);
                        const allValues = [
                            id,
                            ...(indicator.sub_indicators?.map((sub) => sub.code?.toString()) ||
                                []),
                        ].filter(Boolean);

                        const overallChecked =
                            Array.isArray(selectedItems) &&
                            allValues.every((v) => selectedItems.includes(v));

                        return (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                <td>
                                    {/* {indicator.indicator_name && (
                                        <strong className="color-dark-blue f-14">
                                            {[
                                                indicator.indicator_name
                                                    ? `${indicator.indicator_name} (${indicator.dataset_name || ""})`
                                                    : "",
                                                indicator.indicator_date || "",
                                                indicator.agency_name || "",
                                            ]
                                                .filter((val) => val && val !== "NA")
                                                .join(" | ")}
                                            {Array.isArray(indicator.geo_levels) &&
                                                indicator.geo_levels.length > 0 && (
                                                <>
                                                    {" | "}
                                                    {indicator.geo_levels.map((val, idx) => (
                                                        <span key={idx}>
                                                            {val}
                                                            {idx !== indicator.geo_levels.length - 1 && ", "}
                                                        </span>
                                                    ))}
                                                </>
                                            )}
                                        </strong>
                                    )} */}
                                    {indicator.indicator_name && (
                                        <strong className="color-dark-blue f-14">
                                            {[
                                                indicator.indicator_name
                                                    ? `${indicator.indicator_name} (${indicator.dataset_name || ""})`
                                                    : "",
                                                indicator.indicator_date ? (
                                                    <span
                                                        key="date"
                                                        style={{
                                                            backgroundColor: "#007BFF", // Bootstrap blue
                                                            color: "#FFFFFF",
                                                            padding: "2px 6px",
                                                            borderRadius: "4px"
                                                        }}
                                                    >
                                                        {indicator.indicator_date}
                                                    </span>
                                                ) : null,
                                                indicator.agency_name || "",
                                            ]
                                                .filter((val) => val && val !== "NA")
                                                .map((val, i) => (
                                                    <React.Fragment key={i}>
                                                        {i !== 0 && " | "}
                                                        {val}
                                                    </React.Fragment>
                                                ))}

                                            {Array.isArray(indicator.geo_levels) && indicator.geo_levels.length > 0 && (
                                                <>
                                                    {" | "}
                                                    {indicator.geo_levels.map((val, idx) => (
                                                        <span key={idx}>
                                                            {val}
                                                            {idx !== indicator.geo_levels.length - 1 && ", "}
                                                        </span>
                                                    ))}
                                                </>
                                            )}
                                        </strong>
                                    )}
                                    <p className="small f-12 mb-0">
                                        {[
                                            indicator.sector_name,
                                            indicator.sub_sector_name,
                                            indicator.sub_sub_sector_name,
                                        ]
                                            .filter((val) => val && val !== "NA")
                                            .join(" | ")}
                                    </p>
                                </td>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        checked={overallChecked}
                                        onChange={(e) =>
                                            toggleOverall(indicator, e.target.checked)
                                        }
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
            <div className="row mt-2">
                <div className="col-md-8 text-start"><p style={{ 'font-size': '0.85em' }}> <i>Note:</i> Indicators added to the workspace are listed in the list. To include additional indicators, go to the Home page and add the required indicators to the workspace.</p></div>
                <div className="text-end col-md-4 gap-2">
                    <Button
                        className="btn-sm btn-secondary me-2"
                        onClick={handleReset}
                    >
                        Reset
                    </Button>&nbsp;&nbsp;&nbsp;&nbsp;
                    <Button
                        className="btn-sm btn-primary"
                        onClick={handleTableChartSubmit}
                        disabled={indicators.length === 0 || selectedItems.length === 0}
                    >
                        Submit
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TableVisualization;